import { forwardRef, ForwardedRef, ReactNode } from "react";
import type { IconType } from "react-icons";
import { ExternalLinkIcon, Icon } from "@chakra-ui/icons";
import ActiveLink, {
  ActiveLinkProps,
  useIsPathCurrent,
} from "components/Shared/ActiveLink";
import {
  UnavailableIcon,
  UnavailableTooltip,
} from "components/Layout/UnavailableItem";
import SideBarLinkItem from "./LinkItem";
import { PADDING_X } from "./constants";

export type SideBarLinkProps = ActiveLinkProps & {
  IconComponent?: IconType;
  isDisabled?: boolean;
  isUnavailable?: boolean;
  subLinks?: ReactNode;
};

function SideBarLink(
  {
    children,
    IconComponent,
    isDisabled,
    isUnavailable,
    href,
    subLinks,
    exactMatch,
    exactPathname,
    excludedPathnames,
    ...rest
  }: SideBarLinkProps,
  ref: ForwardedRef<HTMLLIElement>,
) {
  const stringHref = typeof href === "string" ? href : undefined;
  const isCurrent = useIsPathCurrent(rest.hrefForCurrent || stringHref, {
    exactMatch,
    exactPathname,
    excludedPathnames,
  });

  return (
    <UnavailableTooltip isUnavailable={isUnavailable}>
      <SideBarLinkItem ref={ref} subLinks={subLinks} isCurrent={isCurrent}>
        <ActiveLink
          href={isDisabled || isUnavailable ? undefined : stringHref}
          isCurrent={isCurrent}
          aria-disabled={isDisabled || isUnavailable || undefined}
          display="block"
          fontWeight={300}
          fontSize=".95em"
          lineHeight="normal"
          py={2}
          px={PADDING_X}
          _activeLink={{
            backgroundColor: "lightPink",
            color: "white",
            _hover: { color: "white" },
          }}
          color="magenta.veryDark"
          isTruncated
          _hover={{ textDecoration: "none" }}
          _disabled={{ cursor: "not-allowed", opacity: 0.6 }}
          {...rest}
        >
          {IconComponent && (
            <Icon
              as={IconComponent}
              opacity={0.7}
              mr={2}
              mb=".1em"
              verticalAlign="text-bottom"
            />
          )}

          {children}

          {isUnavailable && <UnavailableIcon />}

          {rest.isExternal && (
            <ExternalLinkIcon
              ml={1.5}
              fontSize=".8em"
              color="magenta.dark"
              verticalAlign="baseline"
              opacity={0.5}
            />
          )}
        </ActiveLink>
      </SideBarLinkItem>
    </UnavailableTooltip>
  );
}

export default forwardRef(SideBarLink);
